/**
 * VoiceConsentSheet — a ONE-TIME, explicit voice-processing consent gate.
 *
 * Voice sits in Art. 9-adjacent territory (strategy-doc risk #5), so the mic
 * never activates until the founder has explicitly opted in ONCE. The copy is
 * plain and honest: the audio is transcribed then discarded, only the editable
 * transcript is kept, and transcription runs on EU-based servers (Voxtral).
 *
 * Built on the shared `AppModal` primitive (centered card — NOT a bottom sheet,
 * per the modal invariants in CLAUDE.md).
 *
 * Persistence: acceptance is recorded SERVER-SIDE as a `consent_records` row
 * (consent_type 'voice', legal_basis 'consent') — the auditable record DPIA-001
 * Addendum A1 declares for GDPR Art. 6(1)(a). The RLS policy "Users can grant
 * consent" allows the founder's own INSERT; `audio-transcribe` gates on the
 * same row via requireConsent(). The server row is the single source of truth
 * (voice requires network anyway), so there is no local cache to reset on
 * sign-out and consent follows the founder across devices. Prior beta users
 * whose consent lived only in AsyncStorage simply re-see the gate once.
 */

import React from 'react';
import { View, Text } from 'react-native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Mic, ShieldCheck, FileText, MapPin } from 'lucide-react-native';
import { AppModal, ModalAction } from '@/components/modal';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAuth } from '@/lib/supabase/auth';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { mode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import { buildConsentGrantRow } from '@/lib/consent/ai-consent';

/** Keep in sync with CONSENT_VERSIONS.voice + CONSENT_PURPOSES.voice in
 *  supabase/functions/_shared/consent/types.ts (audit-trail consistency).
 *  CONSENT_VERSIONS.voice is pinned at '1.0' (D2): voice is the one type
 *  users have always given through a real prompt (this sheet), so its
 *  existing rows keep counting — no version bump was needed for it.
 *  Exported so AiConsentManagerModal (Settings → Privacy & Security → "AI
 *  processing") can tell whether a stored voice-consent row is CURRENT
 *  without redeclaring this constant a second time. */
export const VOICE_CONSENT_VERSION = '1.0';
const VOICE_CONSENT_PURPOSE =
  'Speech-to-text transcription of voice input during the scoring interview on EU-based servers (Voxtral); audio is processed in-memory and discarded — only the reviewed transcript is kept';

export interface UseVoiceConsent {
  /** True once the founder has accepted (optimistic-safe: false while loading). */
  consented: boolean;
  /** Still reading the stored flag. */
  isLoading: boolean;
  /** Persist acceptance for this user. */
  accept: () => Promise<void>;
}

/**
 * Per-user voice consent, backed by the server-side `consent_records` row
 * (consent_type 'voice'). `consented` is false while loading — the safe
 * direction for a consent gate (it re-shows; it is not an entitlement, so the
 * P0-4 loading-gate invariant does not apply). Mock mode never touches the DB.
 */
export function useVoiceConsent(): UseVoiceConsent {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();
  const queryKey = ['voice-consent', userId] as const;

  const { data, isLoading } = useQuery({
    queryKey,
    enabled: !!userId,
    staleTime: Infinity,
    queryFn: async () => {
      if (!userId) return false;
      if (mode.isMock || !isSupabaseConfigured()) return false;
      const { data: row, error } = await supabase
        .from('consent_records')
        .select('id')
        .eq('user_id', userId)
        .eq('consent_type', 'voice')
        .eq('granted', true)
        .is('withdrawn_at', null)
        .limit(1)
        .maybeSingle();
      if (error) {
        // Fail toward re-showing the gate; never toward silent consent.
        reportError(error, { context: 'useVoiceConsent.query', level: 'warning', silent: true });
        return false;
      }
      return !!row;
    },
  });

  const mutation = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('Cannot record voice consent without a signed-in user');
      if (mode.isMock || !isSupabaseConfigured()) return; // demo/mock: no DB, no real voice path
      // Shared row shape (`buildConsentGrantRow`) with every other
      // consent-granting hook — see `@/lib/consent/ai-consent.ts`.
      const { error } = await supabase.from('consent_records').insert(
        buildConsentGrantRow({
          userId,
          consentType: 'voice',
          version: VOICE_CONSENT_VERSION,
          purpose: VOICE_CONSENT_PURPOSE,
        }),
      );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.setQueryData(queryKey, true);
    },
    onError: (err) => {
      // BLOCKING: audio-transcribe now 403s without the server row, so a
      // failed write must NOT arm the mic. The caller keeps the sheet open;
      // the founder can retry.
      reportError(err, { context: 'useVoiceConsent.accept', level: 'warning' });
    },
  });

  return {
    consented: data === true,
    isLoading: !!userId && isLoading,
    accept: async () => {
      await mutation.mutateAsync();
    },
  };
}

interface VoiceConsentSheetProps {
  visible: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

function ConsentPoint({
  icon,
  text,
  colors,
}: {
  icon: React.ReactNode;
  text: string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View className="flex-row items-start mb-4" style={{ gap: 12 }}>
      <View
        className="w-9 h-9 rounded-xl items-center justify-center"
        style={{ backgroundColor: colors.primary[500] + '1A' }}
      >
        {icon}
      </View>
      <Text
        className="flex-1 text-[14px] leading-5"
        style={{ color: colors.text.secondary, paddingTop: 2 }}
      >
        {text}
      </Text>
    </View>
  );
}

export function VoiceConsentSheet({ visible, onAccept, onDecline }: VoiceConsentSheetProps) {
  const colors = useThemeColors();

  return (
    <AppModal
      visible={visible}
      onClose={onDecline}
      title="Speak your answer"
      footer={
        <AppModal.Actions>
          <ModalAction label="Not now" variant="neutral" onPress={onDecline} />
          <ModalAction
            label="Enable voice"
            variant="primary"
            icon={<Mic size={16} color={colors.text.inverse} />}
            onPress={onAccept}
          />
        </AppModal.Actions>
      }
    >
      <Text
        className="text-[15px] leading-6 mb-5"
        style={{ color: colors.text.primary }}
      >
        Prefer to talk it through? You can speak your answers instead of typing.
        Here is exactly what happens to your voice:
      </Text>

      <ConsentPoint
        colors={colors}
        icon={<ShieldCheck size={17} color={colors.primary[600]} />}
        text="Your voice is turned into text, then the audio is discarded immediately — we never keep a recording."
      />
      <ConsentPoint
        colors={colors}
        icon={<FileText size={17} color={colors.primary[600]} />}
        text="Only the written transcript is kept, and it lands in the message box for you to review and edit before sending."
      />
      <ConsentPoint
        colors={colors}
        icon={<MapPin size={17} color={colors.primary[600]} />}
        text="Transcription runs on EU-based servers (Voxtral). You can switch back to typing at any time."
      />
    </AppModal>
  );
}
